/**
 * Similar CVs Service
 * API calls for finding similar CVs
 */

import axios from 'axios';
import { API_URL as DAB_API_URL } from './api';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export interface ExistingCV {
    id: number;
    filename: string;
    associate_name: string;
    folder_name: string;
}

export interface CVAttributes {
    skills: string[];
    sectors: string[];
    geographic_experience: string[];
    languages: string[];
    years_experience: number | null;
    education_level: string | null;
    methodologies: string[];
    tools: string[];
}

export interface SimilarCVMatch {
    cv_id: number;
    filename: string;
    associate_name: string;
    folder_name: string;
    source_location: string;
    similarity_score: number;
    match_percentage: number;
    match_summary: string;
    key_qualifications: string[];
    geographic_evidence: string[];
    sector_evidence: string[];
    methodology_evidence: string[];
    language_evidence: string[];
    skills_evidence: string[];
}

export interface FindSimilarCVsResult {
    source_cv: {
        filename: string;
        associate_name: string;
        attributes: CVAttributes;
    };
    matches: SimilarCVMatch[];
    total_found: number;
    prioritized_categories?: string[]; // NEW: Categories prioritized based on source CV
    message?: string;
}

export interface FindSimilarCVsRequest {
    cv_id?: number;
    cv_content?: string;
    cv_source: 'associate' | 'iod_staff';
    top_k?: number;
}

/**
 * Remove file extension from name for cleaner display
 * e.g., "Abbey Kim April 2020 CONSENT.docx" -> "Abbey Kim April 2020 CONSENT"
 */
function removeFileExtension(name: string): string {
    if (!name) return 'Unknown';
    // Remove common file extensions
    return name.replace(/\.(docx?|pdf|xlsx?|pptx?|txt)$/i, '').trim();
}

// Cache for all CVs to enable fast client-side search
let cvCache: ExistingCV[] = [];
let cacheLoaded = false;
let cacheLoading: Promise<ExistingCV[]> | null = null;

/**
 * Load all CVs into cache (called once, then filtered client-side)
 */
async function loadCVCache(cvSource: 'associate' | 'iod_staff'): Promise<ExistingCV[]> {
    if (cacheLoaded) return cvCache;
    if (cacheLoading) return cacheLoading;
    
    cacheLoading = (async () => {
        const entityName = cvSource === 'iod_staff' ? 'IODStaffCVContent' : 'CVContent';
        const nameField = cvSource === 'iod_staff' ? 'staff_name' : 'associate_name';
        
        const response = await axios.get(`${DAB_API_URL}/${entityName}`, {
            params: {
                $select: `id,filename,${nameField},folder_name`,
                $orderby: nameField,
                $first: 5000  // DAB uses $first, not $top
            },
            withCredentials: true
        });
        
        const records = response.data?.value || [];
        
        cvCache = records.map((cv: any) => {
            const rawName = cv[nameField] || cv.staff_name || cv.associate_name || 'Unknown';
            const cleanName = removeFileExtension(rawName);
            
            return {
                id: cv.id,
                filename: cv.filename,
                associate_name: cleanName,
                folder_name: cv.folder_name || 'Unknown'
            };
        });
        
        cacheLoaded = true;
        console.log(`Loaded ${cvCache.length} CVs into cache`);
        return cvCache;
    })();
    
    return cacheLoading;
}

/**
 * Search CVs by name - uses cached data with client-side filtering
 * Only queries when searchTerm has 2+ characters
 */
export async function searchCVsByName(searchTerm: string, cvSource: 'associate' | 'iod_staff' = 'associate'): Promise<ExistingCV[]> {
    // Require at least 2 characters to search
    if (!searchTerm || searchTerm.length < 2) {
        return [];
    }
    
    try {
        // Load cache if not already loaded
        const allCVs = await loadCVCache(cvSource);
        
        // Filter client-side (fast since data is cached)
        const searchLower = searchTerm.toLowerCase();
        const filtered = allCVs.filter(cv => 
            cv.associate_name.toLowerCase().includes(searchLower)
        );
        
        // Return top 50 matches
        return filtered.slice(0, 50);
        
    } catch (error: any) {
        console.error('Failed to search CVs:', error);
        throw new Error(`Failed to search CVs: ${error.message}`);
    }
}

/**
 * Get list of existing CVs for selection dropdown
 * @deprecated Use searchCVsByName instead for better performance with large datasets
 */
export async function getExistingCVs(cvSource: 'associate' | 'iod_staff'): Promise<ExistingCV[]> {
    try {
        // Use the Data API Builder endpoint to get CV list
        // DAB uses entity names (CVContent), not table names (cv_content)
        const entityName = cvSource === 'iod_staff' ? 'IODStaffCVContent' : 'CVContent';
        const nameField = cvSource === 'iod_staff' ? 'staff_name' : 'associate_name';
        
        const response = await axios.get(`${DAB_API_URL}/${entityName}`, {
            params: {
                $select: `id,filename,${nameField},folder_name`,
                $orderby: nameField,
                $top: 5000  // Increase limit to get all CVs
            },
            withCredentials: true
        });
        
        const records = response.data?.value || [];
        
        return records.map((cv: any) => {
            // Use associate_name as the primary identifier, remove file extension
            const rawName = cv[nameField] || cv.staff_name || cv.associate_name || 'Unknown';
            const cleanName = removeFileExtension(rawName);
            
            return {
                id: cv.id,
                filename: cv.filename,
                associate_name: cleanName,
                folder_name: cv.folder_name || 'Unknown'
            };
        });
        
    } catch (error: any) {
        console.error('Failed to fetch existing CVs:', error);
        
        // Log more details for debugging
        if (error.response) {
            console.error('Response status:', error.response.status);
            console.error('Response data:', error.response.data);
        }
        
        throw new Error(`Failed to load CV list: ${error.message}`);
    }
}

/**
 * Find similar CVs
 */
export async function findSimilarCVs(request: FindSimilarCVsRequest): Promise<FindSimilarCVsResult> {
    try {
        const response = await axios.post<FindSimilarCVsResult>(
            `${API_URL}/find-similar-cvs`,
            {
                cv_id: request.cv_id,
                cv_content: request.cv_content,
                cv_source: request.cv_source,
                top_k: request.top_k || 5
            },
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true,
                timeout: 120000
            }
        );
        
        return response.data;
        
    } catch (error: any) {
        console.error('Find similar CVs failed:', error);
        
        if (axios.isAxiosError(error)) {
            if (error.code === 'ERR_NETWORK') {
                throw new Error('Service unavailable. Please try again later.');
            }
            if (error.response?.status === 401) {
                throw new Error('Authentication required. Please log in.');
            }
            if (error.response?.status === 404) {
                throw new Error('CV not found in database.');
            }
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
        }
        
        throw new Error('Failed to find similar CVs. Please try again.');
    }
}

export interface SearchByAttributesRequest {
    attributes: Partial<CVAttributes>;
    cv_source: 'associate' | 'iod_staff';
    top_k?: number;
    exclude_cv_id?: number;
}

export interface SearchByAttributesResult {
    matches: SimilarCVMatch[];
    total_found: number;
    search_criteria: Partial<CVAttributes>;
}

export async function searchCVsByAttributes(request: SearchByAttributesRequest): Promise<SearchByAttributesResult> {
    try {
        const response = await axios.post<SearchByAttributesResult>(
            `${API_URL}/search-cvs-by-attributes`,
            {
                attributes: request.attributes,
                cv_source: request.cv_source,
                top_k: request.top_k || 5,
                exclude_cv_id: request.exclude_cv_id
            },
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true,
                timeout: 120000
            }
        );
        
        return response.data;
        
    } catch (error: any) {
        console.error('Search CVs by attributes failed:', error);
        
        if (axios.isAxiosError(error)) {
            if (error.code === 'ERR_NETWORK') {
                throw new Error('Service unavailable. Please try again later.');
            }
            if (error.response?.status === 401) {
                throw new Error('Authentication required. Please log in.');
            }
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
        }
        
        throw new Error('Failed to search CVs by attributes. Please try again.');
    }
}
